import React, {useEffect, useState, useRef} from 'react'
import * as ls from "local-storage";
import {useHistory, useLocation} from 'react-router';
import Api from "../../../api";
import config from '../../../../src/api/config'
import {Button, FormControlLabel, Checkbox, InputAdornment, IconButton} from '@material-ui/core'
import MaterialInput from "../../../components/Inputs/MaterialInput";
import {exitReact, login} from "../../../utils";
import {Visibility, VisibilityOff} from "@material-ui/icons";

import {
    Page,
    Container
} from "./SignUp0.styles";
import SignUpHeader from "../../../components/SignUpHeader";
import HeaderStatusBar from "../../../components/HeaderStatusBar";
import {roles} from "utils/constants";
import {usePersistedState} from "../../../hooks";
import entities from "../../../reduxStore/entityKeys";

// @ts-ignore
const TempStars = window.TempStars;
// @ts-ignore
const app = window.app;
// @ts-ignore
const dataLayer = window?.dataLayer


interface ErrorProps {
    email?: string;
    password?: string;
    refCode?: string;
    terms?: string;
    err?: string;
}

export default function SignUp0() {
    const history = useHistory();
    const { state } = useLocation<{selectedRole: 'office' | 'worker', refCode: string | ''}>();
    const { selectedRole, refCode } = state;
    const [errorMessage, setErrorMessage] = useState<ErrorProps>({});
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const analyticsTracked = useRef(false);

    const setUser = usePersistedState({
        entityName: entities.user,
    })[1];

    useEffect(() => {
        if (!analyticsTracked.current) {
            const eventName = selectedRole === 'office' 
                ? 'view_office_createAccount' 
                : 'view_talent_createAccount';
            TempStars.Analytics.track(eventName, {
                role: selectedRole,
                hasReferralCode: !!refCode
            });
            analyticsTracked.current = true;
        }
    }, [selectedRole, refCode]);

    const handleClickShowPassword = () => {
        setShowPassword((show) => !show);
    }

    const onSubmit = async (event: any) => {
        event.preventDefault();
        const {email, password, inviteCode, confirmedPolicy} = event.target
        let errors = {}

        if (!email.value.length) {
            errors = {...errors, email: 'Email cannot be empty'};
        }
        if (password.value.length < 6) {
            errors = {...errors, password: 'Your password must be at least 6 characters'};
        }
        if (!confirmedPolicy.checked) {
            errors = {...errors, terms: 'You must confirm to continue'};
        }
        if (!email.value.length || !confirmedPolicy.checked || password.value.length < 6)
            return setErrorMessage(errors);

        const userByEmail = await TempStars.Api.findUserByEmail(email.value)
        if (userByEmail.isFound) {
            errors = {...errors, email: 'User with this email already exists'};
            return setErrorMessage(errors)
        }
        try {
            app.showPreloader('Creating Account');
            const {userId} = await Api.createUser({
                email: email.value,
                password: password.value,
                affiliateCode: inviteCode.value,
            })
            TempStars.Analytics.track('sign-up - create account', {userType: selectedRole === 'office'
                    ? 'Dental Office'
                    : 'Dental Worker'
            });
            TempStars.Analytics.setProfileProperties({
                affiliateCode: inviteCode.value,
            })
            await login(email.value, password.value, setUser) //email, password
            TempStars.Analytics.identify( userId );
            TempStars.User.setAnalytics();
            await Api.updateToken()

            // GA
            dataLayer?.push({
                'event' : 'sign_up',
                'mail' : email.value,
            });

            const roleId = selectedRole === 'office' ? roles.DENTIST : roles.HYGIENIST;
            const pathname = selectedRole === 'office' ? 'SignUp1_DO' : 'SignUp1_Talent';
            await Api.createUserRole({roleId, userId: Number(userId)});
            app.hidePreloader();
            return history.push({
                pathname,
            }, {
                userId,
            })

        } catch (e: any) {
            TempStars.Analytics.track('sign-up - error')
            app.hidePreloader();
            const msg = e.toString();
            if (JSON.parse(msg) === "Referral code error") {
                return setErrorMessage({refCode: "Invalid referral code"});
            }
            return setErrorMessage({err: msg});
        }
    }

    const checkboxLabel = <div className="checkboxLabel">
        I confirm that I have read and agree to TempStars <a
        className={"external underlinedLink"} href={`https://${config.web.CA.siteUrl}/terms`}
        target="_system">Terms of Service</a> and <a
        className={"external underlinedLink"} href={`https://${config.web.CA.siteUrl}/privacy`}
        target="_system">Privacy Policy</a>.
    </div>

    const onBack = () => {
        try {
            history.push({
                pathname: 'RoleSelect'
            })
        } catch (e: any) {
            console.log('CANNOT BACK?', e)
        }
    }

    const logIn = () => {
        try {
            history.push({
                pathname: 'Login'
            })
        } catch (e: any) {
            console.log('CANNOT BACK?', e)
        }
    }

    return (
        <Page>
            <Container data-page="signup" className={'no-swipeback'}>
                <HeaderStatusBar onButton={onBack} buttonText={'Back'} withArrow />

                <div className={"bodySignUp"}>

                    <SignUpHeader
                        title={selectedRole === 'office'
                            ? 'Dental Office'
                            : 'Dental Talent'
                        }
                        description={'Sign Up: Step 1 of 3'}
                        error={errorMessage?.err
                            || (errorMessage
                                && (errorMessage?.email || errorMessage?.password || errorMessage?.refCode || errorMessage?.terms)
                                && 'Please fix the errors below')
                        }
                    />

                    <div className="content-block contentBlock">
                        <form id="signup-form" onSubmit={onSubmit}>
                            <div>
                                <MaterialInput
                                    className={'inset'}
                                    label="Email"
                                    type="email"
                                    id={'email'}
                                    name={"email"}
                                    error={!!errorMessage?.email}
                                    helperText={!!errorMessage?.email && errorMessage.email}
                                />
                                <MaterialInput
                                    className={'inset'}
                                    label="Password (at least 6 characters)"
                                    id={'password'}
                                    name={"password"}
                                    type={showPassword ? 'text' : 'password'}
                                    error={!!errorMessage?.password}
                                    helperText={!!errorMessage?.password && errorMessage.password}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                className={'no-fastclick'}
                                                aria-label="visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                <MaterialInput
                                    className={'inset'}
                                    label="Have a Referral Code?"
                                    id={'inviteCode'}
                                    name={"inviteCode"}
                                    type={"text"}
                                    error={!!errorMessage?.refCode}
                                    helperText={errorMessage?.refCode}
                                    defaultValue={refCode || ''}
                                />
                                <div className={'inputTextField inset'}>
                                    <FormControlLabel
                                        className={'no-fastclick'}
                                        control={<Checkbox
                                        name={'confirmedPolicy'}
                                        icon={<img id="officeCheckmark" className="hidden-icon" alt="" src={"img/checkboxUnchecked.svg"} />}
                                        checkedIcon={<img id="officeCheckmark" className="hidden-icon" alt="" src={"img/checkboxChecked.svg"} />}
                                    />} label={checkboxLabel} />
                                    {errorMessage && errorMessage.terms && <div id="agreed-error-msg" className={'errorMsg'}>{errorMessage.terms}</div>}
                                </div>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    id="signup-button"
                                    className="bigButtonInNewStyle"
                                    type={'submit'}
                                >
                                    Create Account
                                </Button>
                            </div>
                        </form>

                        <div className={'toLogIn'}>
                            {`Already have an account? `}
                            <div className="underlinedLink" onClick={logIn}>Log In</div>
                        </div>

                        <div className="helpLinks">
                            <a href={`mailto:${config.CA.helpEmail}`} className="external footerMsg underlinedLink">Contact Support</a>
                        </div>
                    </div>
                </div>

                <div className="footer footerLinks"></div>
            </Container>
        </Page>
    )
}
